import dayjs from "dayjs";
import moment from "moment";
import { colors } from "../../sexy";
import { getFlutterTime } from "../../models/DataFunctions";

// Transform clients into a DataGrid-friendly format
export function transformLogsToTable(
  logs,
  searchvalue = "",
  nopresigned = true,
  noupdate = true,
  nolocalhost = false
) {
  // Define columns for DataGrid, customize based on your data structure

  // remove logs with url contains "presigned"
  if (nopresigned) {
    logs = logs.filter((log) =>
      log?.url && typeof log.url === "string"
        ? !log.url.includes("presigned")
        : true
    );
  }
  if (noupdate) {
    logs = logs.filter((log) =>
      log?.url && typeof log.url === "string"
        ? !log.url.includes("update-doc-data-field")
        : true
    );
  }
  if (nolocalhost) {
    logs = logs.filter((log) =>
      log?.url && typeof log.url === "string"
        ? !log.url.includes("127.0.0.1")
        : true
    );
  }

  const columns = [
    {
      field: "date",
      headerName: "Date",
      width: 150,
      renderCell: (params) => {
        // Customize color based on status value
        const date = params.value;
        return <div>{dayjs.unix(date).format("DD/MM/YYYY HH:mm:ss")}</div>;
      },
    },

    {
      field: "delay",
      headerName: "Délai",
      width: 80,
      renderCell: (params) => {
        const msseconds = params.value;

        if (msseconds === 0) {
          return <div></div>;
        }
        // Customize color based on status value
        return <div>{msseconds + " ms"}</div>;
      },
    },

    { field: "type", headerName: "Type", width: 80 },

    { field: "url", headerName: "Url", width: 250 },

    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => {
        // Customize color based on status value
        const status = params.value;
        let textColor;
        let backgroundColor;

        if (status >= 500) {
          textColor = "#ff0000"; // Error status text color
          backgroundColor = "#ffcccc"; // Error status background
        } else if (status >= 400) {
          textColor = "#ff9900"; // Warning status text color
          backgroundColor = "#fff3e6"; // Warning status background
        } else {
          textColor = "#008000"; // Success status text color
          backgroundColor = "#e6ffe6"; // Success status background
        }

        return (
          <div
            style={{
              backgroundColor: backgroundColor, // Set background color
              color: textColor, // Set text color
              borderRadius: "30px", // Add rounded corners
              padding: "2px 8px", // Add padding
            }}
          >
            <span>{status}</span>
          </div>
        );
      },
    },

    { field: "email", headerName: "Email", width: 250 },

    { field: "org", headerName: "Org", width: 150 },

    // Add more columns as needed
  ];

  if (searchvalue !== "") {
    logs = logs.filter((log) => (log.url + log.email)?.includes(searchvalue));
  }

  // Create rows from the clients array
  const rows = logs.map((log, index) => ({
    id: log.id || index + 1, // Use client's ID or default index
    ref: log.ref || null, // Include client's document reference
    date: log.date.seconds || 0,
    delay: parseInt(log.delay) || 0,

    url:
      typeof log.url === "string"
        ? log.url.replace("https://easy-ath-146636958475.europe-west9.run.app/", "")
        : "",
    type: log.type || "", // Use client's type or default

    status: log.status || "", // Use client's type or default
    org: log.org_id || "", // Use client's city or default
    email: log.email || "", // Use client's city or default
  }));

  return { rows, columns }; // Return rows and columns for the DataGrid
}

// Transform clients into a DataGrid-friendly format
export function transformAppLogsToTable(
  applogs,
  searchvalue = "",
  nopresigned = true,
  noupdate = true
) {
  if (searchvalue !== "") {
    applogs = applogs.filter((log) => log.email?.includes(searchvalue));
  }

  const columns = [
    {
      field: "date",
      headerName: "Date",
      width: 150,
      renderCell: (params) => {
        // Customize color based on status value
        const seconds = params.value;
        return <div>{dayjs.unix(seconds).format("DD/MM/YYYY HH:mm:ss")}</div>;
      },
    },

    { field: "email", headerName: "Type", flex: 1 },

    { field: "length", headerName: "Taille", width: 100 },

    // Add more columns as needed
  ];

  // Create rows from the clients array
  const rows = applogs.map((log, index) => ({
    id: log.id || index + 1, // Use client's ID or default index
    ref: log.ref || null, // Include client's document reference
    date: log.date.seconds || 0,
    email: log.email || "", // Use client's type or default
    length: log.logs.length || 0, // Use client's city or default
  }));

  return { rows, columns }; // Return rows and columns for the DataGrid
}

// Transform clients into a DataGrid-friendly format
export function transformOrgsToTable(orgs) {
  // Define columns for DataGrid, customize based on your data structure

  const columns = [
    { field: "id", headerName: "ID", width: 250 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "name", headerName: "Nom", width: 200 },
    {
      field: "creation",
      headerName: "creation",
      width: 100,
      renderCell: (params) => {
        const creation_date = params.value;
        return (
          <div className="flex flex-row items-center h-full  ">
            {creation_date && new Date(creation_date * 1000).toLocaleString()}
          </div>
        );
      },
    },
    {
      field: "ref",
      headerName: "Ref",
      width: 200,
      renderCell: (params) => {
        const ref = params.value;
        return (
          <div className="flex flex-row items-center h-full  ">
            {ref && ref.id}
          </div>
        );
      },
    },

    {
      field: "ltv",
      headerName: "ltv",
      width: 70,

      renderCell: (params) => {
        const ltv = params.value;
        if (ltv === "") {
          return <></>;
        }
        return (
          <div className="flex flex-row items-center h-full  ">
            <div className="  text-shadow_50 bg-green-100 text-green-400 px-2 text-h2  w-fit  flex rounded-full h-7 items-center ">
              {ltv}
            </div>
          </div>
        );
      },
    },
    {
      field: "plan",
      headerName: "plan",
      width: 150,

      renderCell: (params) => {
        const org_plan = params.value;
        const facturation = params.row.facturation;
        let orgData = params.row.org;
        let engagement = orgData.facturation?.engagement || "none";

        return (
          <div className="flex flex-row items-center h-full  ">
            {" "}
            {org_plan === "free" && (
              <div className="  text-shadow_50 bg-blue-100 text-blue-400 px-2 text-h2  w-fit  flex rounded-full h-7 items-center">
                free ({orgData.counters?.attestations || 0})
              </div>
            )}
            {org_plan === "active" && (
              <div className="  text-shadow_50 bg-blue-100 text-blue-400 px-2 text-h2 w-fit  flex rounded-full h-7 items-center">
                {engagement === "monthly"
                  ? "mensuel"
                  : engagement === "annual"
                  ? "annuel"
                  : "3 ans"}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  // Create rows from the clients array
  const rows = orgs.map((org, index) => ({
    id: org.id || index + 1, // Use client's ID or default index
    ref: org.ref || null, // Include client's document reference
    name: org.infos.name || "", // Use client's name or default 'Unknown'
    email: org.infos.email || "", // Use client's name or default 'Unknown'
    plan: org.facturation?.plan || "free", // Use client's name or default 'Unknown'
    engagement: org.facturation?.engagement || "", // Use client's name or default 'Unknown'
    ltv: org.facturation?.LTV || "", // Use client's name or default 'Unknown'
    creation: org.creation_date?.seconds,
    facturation: org.facturation,
    org: org,
  }));

  return { rows, columns }; // Return rows and columns for the DataGrid
}

// Transform clients into a DataGrid-friendly format
export function transformUsersToTable(users) {
  // Define columns for DataGrid, customize based on your data structure
  const columns = [
    { field: "id", headerName: "ID", width: 250 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "display_name", headerName: "Nom", width: 200 },

    {
      field: "last_connexion",
      headerName: "Last connexion",
      width: 150,
      renderCell: (params) => {
        // check if the date is today

        let isToday = moment(params.value * 1000).isSame(moment(), "day");
        let isYesterday = moment(params.value * 1000).isSame(
          moment().subtract(1, "days"),
          "day"
        );
        let formattedDate = moment(params.value * 1000).format("HH:mm");
        return (
          <>
            {params.value !== 0 && (
              <>
                {isToday && (
                  <div className="text-green-600">
                    Auj
                    <span className="text-shadow_50"> {formattedDate}</span>
                  </div>
                )}
                {isYesterday && (
                  <div className="text-yellow-600">
                    Hier
                    <span className="text-shadow_50"> {formattedDate}</span>
                  </div>
                )}
                {!isToday && !isYesterday && (
                  <div>{new Date(params.value * 1000).toLocaleString()}</div>
                )}
              </>
            )}
          </>
        );
      },
    },

    {
      field: "creation_time",
      headerName: "Creation",
      width: 140,
      renderCell: (params) => {
        return (
          <div
            className={`${
              params.value === 0 ? "text-shadow_25" : "text-shadow_50"
            }`}
          >
            {params.value
              ? new Date(params.value * 1000).toLocaleString()
              : "never"}
          </div>
        );
      },
    },

    {
      field: "isonline",
      headerName: "Online",
      width: 150,
      renderCell: (params) => {
        return (
          <>
            {params.value && (
              <div className="flex flex-row gap-2 w-full items-center">
                <div className="bg-green_simple rounded-full h-3 w-3"></div>
                <div className="">En ligne</div>
              </div>
            )}
            {!params.value && (
              <div className="flex flex-row gap-2 w-full items-center">
                <div className="bg-shadow_25 rounded-full h-3 w-3"></div>
                <div className="text-shadow_50">Hors ligne</div>
              </div>
            )}
          </>
        );
      },
    },

    // Add more columns as needed
  ];

  // Create rows from the clients array
  const rows = users.map((user, index) => ({
    id: user.id || index + 1, // Use client's ID or default index
    ref: user.ref || null, // Include client's document reference
    display_name: user.display_name || "", // Use client's name or default 'Unknown'
    email: user.email || "", // Use client's name or default 'Unknown'
    last_connexion: user?.tracking?.last_connection?.seconds || 0, // Use client's type or default
    feedback: user?.tracking?.feedback,
    creation_time: user.creation_date?.seconds || 0,

    type: user.beta_type || "", // Use client's name or default 'Unknown'
    isonline: user.tracking?.isonline || false, // Use client's city or default
    activity_time: user.tracking?.activity_seconds || 0, // Use client's city or default
  }));

  return { rows, columns }; // Return rows and columns for the DataGrid
}
